import {
	Component,
	EventEmitter,
	HostListener,
	Input,
	OnInit,
	Output
}                        from '@angular/core';
import { BaseComponent } from '@core/bases/base-component/base.component';
import {
	IPreselectedDateItem,
	IYearListItem,
	PositionEnum
}                        from '@shared/components/month-picker/month-picker.model';
import { DateUtils }     from '@utils/helpers';

@Component( {
	selector: 'app-month-picker',
	templateUrl: './month-picker.component.html',
	styleUrls: [ './month-picker.component.scss' ]
} )
export class MonthPickerComponent extends BaseComponent implements OnInit {

	private _clickInside = false;
	private _maxDate: Date;
	private _minDate: Date;
	/** the value emitted on @Output emission */
	private _selectedDate: Date;
	/**
	 * component's "internal" year value, used as fallback for `activeYear`
	 */
	private _selectedYear: number;
	private _preselectedDateList: IPreselectedDateItem[];
	private _yearRange = 10;

	/**
	 * year value as selected in widget, used to create the year dropdown lists
	 * and in @Output emission
	 */
	public activeYear: number;
	/** month value as selected in widget, used in @Output emission */
	public activeMonth: number;
	public isHovered: boolean;
	public isOpen = false;
	public monthNumberList = new Array( 12 );
	public positionEnum = PositionEnum;
	/**
	 * component's "internal" combined year and month value, used for comparison
	 * in view and as fallback for `activeMonth`
	 */
	public selectedYearMonth: string;
	public yearList: IYearListItem[] = [];


	@Input() dateFormat: Intl.DateTimeFormatOptions;

	get maxDate(): Date {
		return this._maxDate;
	}

	@Input() set maxDate( value: Date ) {
		this._maxDate = value;
		this.createYearRange();
	}

	get minDate(): Date {
		return this._minDate;
	}

	@Input() set minDate( value: Date ) {
		this._minDate = value;
		this.createYearRange();
	}

	get preselectedDateList(): IPreselectedDateItem[] {
		return this._preselectedDateList;
	}

	@Input() set preselectedDateList( preselectedDateList: IPreselectedDateItem[] ) {
		this._preselectedDateList = preselectedDateList?.map( ( preselectedDateItem: IPreselectedDateItem ) => {
			const date: Date = DateUtils.getFirstDateOfMonth( preselectedDateItem?.date );
			return {
				...preselectedDateItem,
				date
			};
		} );

		this.updatePreselectedDateSelection();
	}

	@Input() positionLabel;
	@Input() positionPickerMenu;
	@Input() readOnlyYear = false;

	get selectedDate(): Date {
		return this._selectedDate;
	}

	@Input() set selectedDate( date: Date ) {
		if ( !date ) {
			if ( this._selectedDate ) {
				this.reset();
			}

		} else {
			const year = date?.getFullYear();
			const month = date?.getMonth();

			this._selectedYear = year;
			this.selectedYearMonth = `${ year }${ month }`;

			this._selectedDate = new Date( year, month );

			this.setActiveYearAccordingToAllowedRange( year );

			if ( this.isMonthInAllowedRange( month ) ) {
				this.setActiveMonth( month );
			}
		}

		this.updatePreselectedDateSelection();
	}

	@Input() showDateLabel;
	@Input() showMonthItems;


	@Output() selectedMonthDateEmitter: EventEmitter<Date> = new EventEmitter();


	@HostListener( 'click' )
	clickInside() {
		this._clickInside = true;
	}

	@HostListener( 'document:click' )
	clickOutside() {
		if ( this.isOpen && !this._clickInside ) {
			this.toggle();
		}
		this._clickInside = false;
	}

	constructor() {
		super();
	}

	private applyFallbacks(): void {
		const applyOnCloseFallback = this.isOpen &&
																 this.activeMonth === null &&
																 this._selectedYear &&
																 !!this.selectedYearMonth;

		const applyOnOpenFallback = !this.isOpen &&
																!this._selectedYear &&
																!this.selectedYearMonth;

		if ( applyOnCloseFallback ) {
			/*
			 * apply fallback after nulling of activeMonth on active year change
			 * (for making the same month selectable in that other year)
			 * without actual selection of a new month
			 */
			this.setActiveYearAccordingToAllowedRange( this._selectedYear );
			this.setActiveMonth( Number.parseInt( this.selectedYearMonth.slice( 4 ), 10 ) );
			return;
		}

		if ( applyOnOpenFallback ) {
			// apply fallback after complete reset of component
			this.setActiveYear( new Date().getFullYear(), true, true );
		}
	}

	private createYearRange( activeYear?: number ): void {
		this.yearList = [];
		const startYearRange = this.activeYear - this._yearRange;
		const endYearRange = this.activeYear + this._yearRange;

		for ( let i = startYearRange; i <= endYearRange; i++ ) {
			if ( !this.isYearSmallerMinYear( i ) && !this.isYearGreaterEqualMaxYear( i ) ) {
				this.yearList.push( { year: i, disabled: false } );
			}
		}

		if ( this.yearList.length && this._selectedYear ) {

			if ( this._selectedYear < this.yearList[ 0 ].year ) {
				this.yearList.unshift( { year: this._selectedYear, disabled: !this.showMonthItems } );
			}

			if ( this._selectedYear > this.yearList[ this.yearList.length - 1 ].year ) {
				this.yearList.push( { year: this._selectedYear, disabled: !this.showMonthItems } );
			}

			this.setActiveYear( activeYear ?? this._selectedYear, false );
		}
	}

	private isDateSmallerMinDate( date: Date ): boolean {
		return !!this.minDate && date < this.minDate;
	}

	private isDateGreaterEqualMaxDate( date: Date ): boolean {
		return !!this.maxDate && date >= this.maxDate;
	}

	private setActiveYearAccordingToAllowedRange( year: number ): void {
		if ( this.isYearInAllowedRange( year ) ) {
			this.setActiveYear( year );
		} else {
			this.setActiveYear( new Date().getFullYear(), true, true );
		}
	}

	private setActiveYear(
		year: number,
		createYearRange = true,
		useYearAsReferenceForRange?: boolean
	): void {
		this.activeYear = year;
		if ( createYearRange ) {
			this.createYearRange( useYearAsReferenceForRange ? year : null );
		}
	}

	private isYearInAllowedRange( year: number ): boolean {
		return !this.isYearSmallerMinYear( year ) && !this.isYearGreaterEqualMaxYear( year );
	}

	private setActiveMonth( month: number ): void {
		this.activeMonth = month;
	}

	private isMonthInAllowedRange( month: number ): boolean {
		return !this.isMonthSmallerMinMonth( month ) && !this.isMonthGreaterEqualMaxMonth( month );
	}

	private reset(): void {
		this._selectedYear = undefined;
		this._selectedDate = undefined;
		this.selectedYearMonth = undefined;
		this.setActiveMonth( undefined );
		this.setActiveYear( new Date().getFullYear() );
	}

	private updatePreselectedDateSelection(): void {
		if ( this.preselectedDateList?.length ) {
			this.preselectedDateList.forEach( ( dateItem: IPreselectedDateItem ) => {
				dateItem.isActive = false;

				const dateItemYear = dateItem.date.getFullYear();
				const dateItemMonth = dateItem.date.getMonth();
				dateItem.isActive = dateItemYear === this.activeYear && dateItemMonth === this.activeMonth;
			} );
		}
	}

	private updateAndEmitSelectedDate(): void {
		this._selectedDate = new Date( this.activeYear, this.activeMonth );
		this.updatePreselectedDateSelection();
		this.selectedMonthDateEmitter.emit( this._selectedDate );
	}

	private updateSelectedYearMonth(): void {
		this.selectedYearMonth = `${ this.activeYear }${ this.activeMonth }`;
	}

	ngOnInit(): void {
		this.positionLabel = this.positionLabel ?? PositionEnum.RIGHT;
		this.positionPickerMenu = this.positionPickerMenu ?? PositionEnum.RIGHT;
		this.showDateLabel = this.showDateLabel ?? false;
		this.showMonthItems = this.showMonthItems ?? true;
		this.dateFormat = this.dateFormat ?? { month: 'long', year: 'numeric' };
	}

	toggle(): void {
		this.applyFallbacks();
		this.isOpen = !this.isOpen;
	}

	isYearSmallerMinYear( year: number ): boolean {
		return this.isDateSmallerMinDate( new Date( year, 11 ) );
	}

	isYearGreaterEqualMaxYear( year: number ): boolean {
		return this.isDateGreaterEqualMaxDate( new Date( year, 0 ) );
	}

	isMonthSmallerMinMonth( month: number ): boolean {
		return this.isDateSmallerMinDate( new Date( this.activeYear, month ) );
	}

	isMonthGreaterEqualMaxMonth( month: number ): boolean {
		return this.isDateGreaterEqualMaxDate( new Date( this.activeYear, month ) );
	}

	prevYear(): void {
		if ( !this.isYearSmallerMinYear( this.activeYear - 1 ) ) {
			this.selectYear( this.activeYear - 1 );
		}
	}

	nextYear(): void {
		if ( !this.isYearGreaterEqualMaxYear( this.activeYear + 1 ) ) {
			this.selectYear( this.activeYear + 1 );
		}
	}

	selectYear( year: number ): void {
		const isYearInAllowedRange = this.isYearInAllowedRange( year );

		if ( isYearInAllowedRange && !this.showMonthItems ) {
			this.setActiveYear( year, true, true );
			this.updateAndEmitSelectedDate();
		} else if ( this.yearList.some( ( yearItem: IYearListItem ) => yearItem.year === year ) ) {
			this.setActiveYear( year, true, true );
			this.setActiveMonth( null );
		}

		if ( !this.showMonthItems ) {
			this.toggle();
		}
	}

	selectMonth( month: number ): void {
		const isMonthInAllowedRange = this.isMonthInAllowedRange( month );
		if ( isMonthInAllowedRange ) {
			this.setActiveMonth( month );
			this.updateAndEmitSelectedDate();
			this.isOpen = false;
		}

		this.updateSelectedYearMonth();
	}

	onPreselectedDateSelection( preselectedDateItem: IPreselectedDateItem ): void {
		if ( preselectedDateItem?.isActive ) { return; }

		const year = preselectedDateItem?.date?.getFullYear();
		const month = preselectedDateItem?.date?.getMonth();
		const isYearInAllowedRange = this.isYearInAllowedRange( year );
		let dateIsCheckedAndSet = false;

		if ( this.showMonthItems ) { // year and month
			/*
			 * TODO Doesn't work properly for "Current Month" button because following
			 * flag is set to `false` if activeYear is year of minDate and current
			 * month is outside allowed range in that year!
			 * Month checker function has to test for 'year' too!
			 */
			const isMonthInAllowedRange = this.isMonthInAllowedRange( month );
			if ( isYearInAllowedRange && isMonthInAllowedRange ) {
				this.setActiveMonth( month );
				this.setActiveYear( year, false );
				dateIsCheckedAndSet = true;
			}
		} else { // year only
			if ( isYearInAllowedRange ) {
				this.setActiveYear( year, false );
				dateIsCheckedAndSet = true;
			}
		}

		if ( dateIsCheckedAndSet ) {
			this.preselectedDateList.forEach( ( dateItem: IPreselectedDateItem ) => {
				dateItem.isActive = false;
				dateItem.isActive = dateItem.label === preselectedDateItem?.label;
			} );

			this.updateAndEmitSelectedDate();
			this.isOpen = false;

			this.logger.log( preselectedDateItem?.label );
		}
	}
}
